import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Settings2 } from "lucide-react";
import { toast } from "sonner";

const WIDGETS = [
  { key: "kpis", label: "KPI strip", desc: "Jobs live, timesheets pending, open safety…" },
  { key: "financials", label: "Xero financials", desc: "Cash position snapshot (admins only)" },
  { key: "setup_checklist", label: "Setup checklist", desc: "Company setup progress card" },
  { key: "quick_actions", label: "Quick actions", desc: "Review hours / triage safety / dispatch shortcuts" },
  { key: "fleet_alerts", label: "Fleet compliance alerts", desc: "WoF, rego and service warnings" },
  { key: "plant_service", label: "Plant service due", desc: "Machines approaching service hours" },
  { key: "weather", label: "Weather risk", desc: "Site weather warnings" },
  { key: "activity_feed", label: "Live activity feed", desc: "What the team is doing right now" },
  { key: "alert_panels", label: "Alerts / Announcements / Reminders", desc: "Simpro-style panel row at the top" },
  { key: "crew_map", label: "Crew live map", desc: "Today's GPS clock-in locations on a map" },
  { key: "charts_hours_day", label: "Chart: crew hours / day", desc: "Labour hours across the last 28 days" },
  { key: "charts_hours_job", label: "Chart: hours by job", desc: "Top 5 jobs by labour, last 30 days" },
  { key: "charts_invoicing", label: "Chart: invoicing", desc: "Invoiced vs paid, last 6 months" },
  { key: "charts_safety", label: "Chart: safety pulse", desc: "Pre-starts per week, unsafe flagged" },
  { key: "charts_fleet", label: "Chart: fleet hours", desc: "Machine hours this week from timesheets" },
  { key: "missing_timesheets", label: "Missing timesheets", desc: "Who hasn't logged hours (Timesheets page)" },
  { key: "chat_email_notifications", label: "Chat mention emails", desc: "Email teammates when they're @mentioned in chat (off = in-app/push only)" },
];

/**
 * Admin-only "Customise dashboard" gear + dialog. Toggles are org-wide —
 * what the company doesn't want to see disappears for everyone.
 */
export function DashboardSettings({ prefs, onChanged }) {
  const [open, setOpen] = useState(false);
  const [local, setLocal] = useState(prefs || {});
  const [saving, setSaving] = useState(false);

  useEffect(() => { setLocal(prefs || {}); }, [prefs]);

  const flip = (key) => setLocal((p) => ({ ...p, [key]: !(p[key] !== false) }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/org/ui-prefs", { prefs: local });
      toast.success("Dashboard updated for your whole company");
      onChanged?.(data);
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)}
              className="h-8 rounded-sm border-zinc-300 text-[10px] font-bold uppercase tracking-widest text-zinc-600"
              data-testid="dashboard-settings-btn">
        <Settings2 className="w-3.5 h-3.5 mr-1.5" /> Customise
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-md" data-testid="dashboard-settings-dialog">
          <DialogHeader>
            <DialogTitle className="font-display font-bold uppercase tracking-tight">Customise dashboard</DialogTitle>
            <DialogDescription className="text-xs">
              Turn off anything your company doesn't use. Applies to everyone in your org.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1 max-h-96 overflow-y-auto">
            {WIDGETS.map((w) => (
              <label key={w.key}
                     className="flex items-center gap-3 p-2.5 border border-zinc-200 cursor-pointer hover:border-zinc-400 transition-colors"
                     data-testid={`dashboard-setting-row-${w.key}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-bold">{w.label}</div>
                  <div className="text-[11px] text-zinc-500">{w.desc}</div>
                </div>
                <Switch checked={local[w.key] !== false} onCheckedChange={() => flip(w.key)}
                        data-testid={`dashboard-setting-toggle-${w.key}`} />
              </label>
            ))}
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}
                    data-testid="dashboard-settings-cancel">Cancel</Button>
            <Button onClick={save} disabled={saving}
                    className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] font-bold uppercase tracking-wider"
                    data-testid="dashboard-settings-save">
              {saving ? "Saving…" : "Save"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
